/**
 * Notificaciones efímeras. Almacén simple fuera de React (patrón `useSyncExternalStore`, igual
 * que `src/api/session.ts`) para poder llamar `toast.success(...)`/`toast.error(...)` desde
 * cualquier `catch` sin pasar por props ni por Context.
 */
import { useSyncExternalStore, type ReactElement } from "react";

export type ToastTone = "success" | "error" | "info";

interface ToastEntry {
  id: number;
  tone: ToastTone;
  message: string;
}

let entries: ToastEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ToastEntry[] {
  return entries;
}

function push(tone: ToastTone, message: string): void {
  const id = nextId++;
  entries = [...entries, { id, tone, message }];
  notify();
  setTimeout(() => dismiss(id), 5000);
}

function dismiss(id: number): void {
  entries = entries.filter((entry) => entry.id !== id);
  notify();
}

export const toast = {
  success: (message: string) => push("success", message),
  error: (message: string) => push("error", message),
  info: (message: string) => push("info", message),
};

/** Se monta una única vez en `App.tsx`, fuera de las rutas, para persistir entre navegaciones. */
export function ToastHost(): ReactElement {
  const list = useSyncExternalStore(subscribe, getSnapshot);
  return (
    <div className="toast-host" role="status" aria-live="polite">
      {list.map((entry) => (
        <div key={entry.id} className={`toast toast-${entry.tone}`}>
          <span>{entry.message}</span>
          <button type="button" className="icon-button" aria-label="Descartar" onClick={() => dismiss(entry.id)}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
